import { useMemo } from "react";

import { IStaffJob } from "@/api";
import {
  useHealthSystemStaffJobs,
  useStaffCategoryByStaffTypeKey,
  useUnitStaffJobs,
} from "@/common/AppConfigUtils";
import { useMap } from "@/common/hooks";

import { TAutocompleteOption, TStaffJobPickerProps } from "../types";

export const useGetOptionsAndSelection = <
  TMultiple extends boolean,
  TValue extends TMultiple extends true ? IStaffJob["id"][] : IStaffJob["id"],
  TOption extends TMultiple extends true ? TAutocompleteOption[] : TAutocompleteOption,
>(
  props: TStaffJobPickerProps<TMultiple, TValue>,
) => {
  const unitStaffJobs = useUnitStaffJobs(props.unitId);
  const allStaffJobs = useHealthSystemStaffJobs(props.healthSystemId);
  const staffJobs = props.unitId ? unitStaffJobs : allStaffJobs;
  const staffCategoryByStaffTypeKey = useStaffCategoryByStaffTypeKey();

  const options = useMap(
    staffJobs,
    (staffJob) => ({
      label: staffJob.name,
      staffCategory: staffCategoryByStaffTypeKey[staffJob.staffTypeKey]!,
      selected: props.value?.includes(staffJob.id) || props.value === staffJob.id,
      value: staffJob,
    }),
    { sortBy: ["staffCategory.name"] },
  );

  const selection = useMemo(() => {
    if (props.multiple)
      return options.filter((option) => props.value?.includes(option.value.id)) as TOption;

    return options.find((option) => option.value?.id === props.value) as TOption;
  }, [props.value, props.multiple, options]);

  return useMemo(() => ({ options, selection }), [options, selection]);
};
